import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import AgentSearchSelect from '../../../components/form/AgentSearchSelect';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import { createEmptyAgentRouteRow } from '../constants';

function AgentRoutesEditor({
  id = 'agent-routes',
  label = 'Destinos (agentes)',
  hint = 'Defina cada agente de destino, o rótulo do botão WhatsApp e palavras-chave para roteamento. Use o ícone de IA na descrição para refinar o escopo de cada destino.',
  routes = [],
  onChange,
  allowedAgentIds = null,
  error,
  onRequestImproveDescription,
  improvingRouteId = null,
  isImproveBusy = false,
  getRouteDescriptionImproveError,
}) {
  const usedAgentIds = new Set(
    routes.map((route) => route.agentId).filter(Boolean)
  );

  const handleAdd = () => {
    onChange([...routes, createEmptyAgentRouteRow()]);
  };

  const handleRemove = (routeId) => {
    onChange(routes.filter((route) => route.id !== routeId));
  };

  const handleUpdate = (routeId, patch) => {
    onChange(
      routes.map((route) =>
        route.id === routeId ? { ...route, ...patch } : route
      )
    );
  };

  const handleAgentChange = (routeId) => (event) => {
    const agentId = event?.target?.value ?? '';
    if (agentId && usedAgentIds.has(agentId)) {
      const current = routes.find((route) => route.id === routeId);
      if (current?.agentId !== agentId) {
        return;
      }
    }
    handleUpdate(routeId, { agentId });
  };

  return (
    <div>
      {label && (
        <p className="block text-sm font-medium text-slate-700 mb-2">{label}</p>
      )}

      <div
        id={id}
        className={`space-y-4 ${error ? 'rounded-xl border border-red-300 p-3' : ''}`}
      >
        {routes.length === 0 && (
          <p className="text-sm text-content-muted">
            Adicione pelo menos um agente de destino para o roteamento inbound.
          </p>
        )}

        {routes.map((route, index) => {
          const isDuplicate =
            route.agentId &&
            routes.filter((item) => item.agentId === route.agentId).length > 1;
          const agentNotInGroup =
            route.agentId &&
            allowedAgentIds &&
            allowedAgentIds.length > 0 &&
            !allowedAgentIds.includes(route.agentId);

          return (
            <div
              key={route.id}
              className="rounded-xl border border-surface-border bg-white p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <AgentSearchSelect
                    id={`${id}_agent_${route.id}`}
                    label={`Agente ${index + 1}`}
                    value={route.agentId}
                    onChange={handleAgentChange(route.id)}
                    layout="stacked"
                  />
                  {(isDuplicate || agentNotInGroup) && (
                    <p className="mt-1 text-xs text-red-600">
                      {isDuplicate
                        ? 'Este agente já foi adicionado.'
                        : 'Este agente não pertence ao WhatsApp selecionado.'}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(route.id)}
                  className="mt-7 p-2 text-content-muted hover:text-red-600 transition-colors"
                  aria-label="Remover destino"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <Input
                id={`${id}_label_${route.id}`}
                label="Rótulo (botão WhatsApp)"
                value={route.label}
                onChange={(event) =>
                  handleUpdate(route.id, { label: event.target.value })
                }
                placeholder="Ex.: Suporte, Vendas"
                required
              />

              <Textarea
                id={`${id}_description_${route.id}`}
                label="Descrição (escopo para IA)"
                value={route.description}
                onChange={(event) =>
                  handleUpdate(route.id, { description: event.target.value })
                }
                placeholder="Descreva quando direcionar mensagens para este agente..."
                rows={2}
                hint="Usada pela IA para decidir quando encaminhar para este destino. Descreva assuntos e o que fica de fora, não o comportamento do agente, e evite dizer que o destino atende todas as conversas. Use o ícone de IA para refinar o texto."
                action={
                  onRequestImproveDescription
                    ? {
                        iconOnly: true,
                        ariaLabel: `Melhorar descrição do destino ${route.label || index + 1} com IA`,
                        loading: improvingRouteId === route.id,
                        disabled: isImproveBusy,
                        onClick: () => onRequestImproveDescription(route),
                        error: getRouteDescriptionImproveError?.(route.id),
                      }
                    : undefined
                }
              />

              <Input
                id={`${id}_keywords_${route.id}`}
                label="Palavras-chave"
                value={route.keywordsInput}
                onChange={(event) =>
                  handleUpdate(route.id, { keywordsInput: event.target.value })
                }
                placeholder="suporte, pedido, entrega"
                hint="Separe por vírgula. Usadas no roteamento por palavras-chave."
              />
            </div>
          );
        })}

        <button type="button" onClick={handleAdd} className="btn-secondary text-sm">
          <Plus className="h-4 w-4 mr-1.5 inline" />
          Adicionar destino
        </button>
      </div>

      {hint && !error && (
        <p className="mt-1.5 text-xs text-content-muted">{hint}</p>
      )}
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default AgentRoutesEditor;
